import moment from "moment";
import { t } from "i18next";
import useLang from "Shared/hooks/useLanguage";
import { resolveSignedByDisplayName } from "Utils/Requisition/resolveSignedByDisplayName";

function getSiblingFieldValue(fieldData: unknown, systemFieldName: string): string {
  if (!Array.isArray(fieldData)) return "";
  const row = fieldData.find(
    (x: { systemFieldName?: string }) => x?.systemFieldName === systemFieldName
  );
  if (!row || row.fieldValue === null || row.fieldValue === undefined) return "";
  return String(row.fieldValue).trim();
}
function getSiblingSelectedText(fieldData: unknown, systemFieldName: string): string {
  if (!Array.isArray(fieldData)) return "";
  const row = fieldData.find(
    (x: { systemFieldName?: string }) => x?.systemFieldName === systemFieldName
  );
  if (!row || row.selectedText === null || row.selectedText === undefined) return "";
  return String(row.selectedText).trim();
}

function signatureImgSrc(fieldValue: unknown): string {
  if (fieldValue === null || fieldValue === undefined) return "";
  const raw = String(fieldValue);
  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("data:")
  ) {
    return raw;
  }
  if (raw.includes("data:image/png;base64,")) {
    return raw;
  }
  return `data:image/png;base64,${raw}`;
}

function convertTo12HourFormat(time: string) {
  const timeStr = String(time || "");
  if (!timeStr || !timeStr.includes(":")) return timeStr || "";
  const parts = timeStr.split(":").map(Number);
  const hours = parts[0];
  const minutes = parts[1] ?? 0;
  const period = hours >= 12 ? "PM" : "AM";
  const adjustedHours = hours % 12 || 12;
  const paddedMinutes = String(minutes).padStart(2, "0");
  return `${adjustedHours}:${paddedMinutes} ${period}`;
}

const PPSignature = ({ signatureData }: any) => {
  const { t: tLang } = useLang();
  const physicianName = (
    sessionStorage.getItem("PhysicianName") || ""
  ).trim();

  const renderFieldValue = (field: any) => {
    const value = field?.fieldValue;
    const systemName = field?.systemFieldName ?? "";
    const isEmpty = value === undefined || value === null || value === "";

    if (isEmpty) return <span className="text-muted">—</span>;

    if (systemName === "TimeofCollection" || (typeof value === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value.trim()))) {
      return convertTo12HourFormat(value);
    }
    if (systemName === "DOB" || (typeof value === "string" && moment(value, moment.ISO_8601, true).isValid())) {
      return moment(value).format("YYYY-MM-DD");
    }
    if (typeof value === "string" && value.length > 0 && moment(value, moment.ISO_8601, true).isValid()) {
      return moment(value).format("MM/DD/YYYY hh:mm A");
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    return String(value);
  };

  const allFields = signatureData?.fields ?? [];
  const signatureField = allFields.find(
    (f: any) =>
      f?.systemFieldName === "PatientSignature" ||
      f?.systemFieldName === "PhysicianSignature"
  );
  const isPhysicianSignature = signatureField?.systemFieldName === "PhysicianSignature";
  const providerName = resolveSignedByDisplayName(
    getSiblingSelectedText(allFields, "SignedBy"),
    allFields
  );
  const sigDate = getSiblingFieldValue(allFields, "PhysicianDate");
  const sigTimeRaw = getSiblingFieldValue(allFields, "PhysicianTime");
  const sigTimeDisplay = sigTimeRaw ? convertTo12HourFormat(sigTimeRaw) : "";
  const showElectronicAttestation =
    isPhysicianSignature &&
    (providerName !== "" || sigDate !== "" || sigTimeRaw !== "");

  const metaFields = allFields.filter((f: any) => {
    if (
      f?.systemFieldName === "PatientSignature" ||
      f?.systemFieldName === "PhysicianSignature"
    ) {
      return false;
    }
    if (showElectronicAttestation) {
      if (
        f?.systemFieldName === "SignedBy" ||
        f?.systemFieldName === "PhysicianDate" ||
        f?.systemFieldName === "PhysicianTime"
      ) {
        return false;
      }
    }
    return true;
  });
  return (
    <div
      key={signatureData?.sectionName}
      className="col-lg-6 col-md-6 col-sm-12 px-3"
    >
      <div className="card border-gray-500 shadow-xs mb-3 overflow-hidden">
        <div className="align-items-center card-header d-flex fs-5 fw-600 min-h-40px px-4 text-dark bg-light border-bottom">
          {tLang(signatureData?.sectionName)}
        </div>
        <div className="card-body p-4">
          {metaFields.length > 0 && (
            metaFields.map((field: any, idx: number) => (
              <div
                key={field?.controlId ?? field?.displayName}
                className={`d-flex align-items-baseline justify-content-between gap-3 py-2 ${idx < metaFields.length - 1 ? "border-bottom" : ""}`}
                style={
                  idx < metaFields.length - 1
                    ? { borderBottomColor: "rgba(0,0,0,.08)" }
                    : undefined
                }
              >
                <span className="fw-600 text-body-secondary small text-uppercase">
                  {tLang(field?.displayName)}
                </span>
                <span className="fs-6 text-end text-break">
                  {renderFieldValue(field)}
                </span>
              </div>
            ))
          )}
          {signatureField && (
            <div className="mt-2">
              <span className="d-block fw-600 text-body-secondary small text-uppercase mb-2">
                {tLang("Signature")}
              </span>
              <div className="d-inline-block p-3 rounded border bg-white" style={{ minHeight: "80px" }}>
                {typeof signatureField.fieldValue === "string" &&
                  (signatureField.fieldValue.startsWith("data:image/png;base64,") ||
                    signatureField.fieldValue.startsWith("http://") ||
                    signatureField.fieldValue.startsWith("https://")) ? (
                  <img
                    src={signatureImgSrc(signatureField.fieldValue)}
                    alt=""
                    className="d-block"
                    style={{ maxWidth: "320px", height: "auto", minWidth: "200px" }}
                  />
                ) : typeof signatureField.fieldValue === "string" ? (
                  <span className="fs-6">{atob(signatureField.fieldValue)}</span>
                ) : null}
              </div>
              {showElectronicAttestation ? (
                <p className="mt-3 mb-0">
                  {t("Signed by")}{" "}
                  <strong className="fw-bold text-body">
                    {physicianName}
                  </strong>{" "}
                  {t("on")}{" "}
                  <strong className="fw-bold text-body">{sigDate || "—"}</strong>{" "}
                  {t("and")}{" "}
                  <strong className="fw-bold text-body">
                    {sigTimeDisplay || sigTimeRaw || "—"}
                  </strong>
                  .
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PPSignature;
